import { sessionlist, grpplayerlist, homepageadmin, getTablePlanning } from "../components/funtionsBD/Mainservice";
import moment from "moment";
import { mapHomeRoleToUserType } from "../utils/homeRole";
import { readActiveHomeRole } from "../utils/homeRole";
import { isCoachViewForTraining } from "../utils/trainingRoomAccess";
import { isViewerPresentInSession, resolveViewerPlayerId } from "../utils/trainingRoomAccess";
import {
  isSessionFinalized,
  isSessionTodayOrTomorrow,
  parseSessionWindow,
  pickBestEligibleSession,
  isSessionJoinableSoon,
  shouldEnterLiveTrainingAfterPresence,
} from "../utils/liveTrainingSessionWindow";
import { writeLiveTrainingContext } from "../utils/liveTrainingContext";
import { normalizeStoredGroupInfo } from "../utils/liveTrainingGroupInfo";
import { clearCoachLiveTrainingAutoEnterSuppression } from "../utils/liveTrainingNav";
import { addCalendarDays, formatCalendarDay } from "../utils/sessionCalendarAgenda";

function normalizeSession(raw, gr_id) {
  if (!raw) return null;
  return {
    ...raw,
    ts_id: raw.ts_id || raw.id,
    gr_id: raw.gr_id || gr_id,
  };
}

async function isPlayerRegistered(session, user) {
  const grId = session.gr_id;
  const tsId = session.ts_id;
  if (!grId || !tsId) return false;
  try {
    const res = await grpplayerlist({ ts_id: tsId, gr_id: grId });
    const roster = res?.data?.data || [];
    const playerId = resolveViewerPlayerId(user, roster);
    return isViewerPresentInSession(playerId, roster);
  } catch {
    return false;
  }
}

async function isUserEligibleForSession(session, user, permissions) {
  if (isSessionFinalized(session)) return false;
  // Coaches (and players after presence) enter Live Training from 30 min before
  // start through the scheduled window end — not only after the clock starts.
  if (!shouldEnterLiveTrainingAfterPresence(session)) return false;

  const coachView = isCoachViewForTraining(user, permissions || []);
  // Any connected session conductor (coach / monitor / …) may enter and control
  // Live Training — including a second coach or monitor not listed on tbl_ts_coach.
  if (coachView) return true;
  return isPlayerRegistered(session, user);
}

async function collectGroupSessions(groupIds) {
  try {
    const today = formatCalendarDay();
    const tomorrow = addCalendarDays(new Date(), 1);
    const res = await sessionlist({
      gr_ids: groupIds,
      date_from: today,
      date_to: tomorrow,
      include_hr: false,
    });
    const list = res?.data?.group || [];
    const allSessions = list
      .map((s) => normalizeSession(s, s.gr_id))
      .filter((s) => isSessionTodayOrTomorrow(s));
    const byTs = new Map();
    allSessions.forEach((s) => {
      if (s?.ts_id && !byTs.has(s.ts_id)) byTs.set(s.ts_id, s);
    });
    return [...byTs.values()];
  } catch {
    return [];
  }
}

function occupancyToSession(row) {
  if (!row?.ts_id || !row?.starts_at) return null;
  const start = moment(row.starts_at);
  if (!start.isValid()) return null;
  const end = row.ends_at ? moment(row.ends_at) : start.clone().add(90, "minutes");
  const durationMin = Math.max(1, end.diff(start, "minutes") || 90);
  return normalizeSession(
    {
      ts_id: row.ts_id,
      gr_id: row.gr_id,
      ts_date: start.format("YYYY-MM-DD"),
      ts_start_time: start.format("HH:mm:ss"),
      ts_duration: durationMin,
      ts_owner_player_id: row.ts_owner_player_id || true,
      tbl_state_te: row.tbl_state_te || row.state_te,
    },
    row.gr_id
  );
}

async function collectPlayerOwnedSessions(user) {
  const clubId = user?.club_id;
  const playerId = user?.player_id != null ? String(user.player_id) : null;
  if (!clubId || !playerId) return [];
  try {
    const today = formatCalendarDay();
    const tomorrow = addCalendarDays(new Date(), 1);
    const res = await getTablePlanning({
      club_id: clubId,
      from: today,
      to: tomorrow,
    });
    const body = res?.data;
    const data =
      body?.data && typeof body.data === "object" && !Array.isArray(body.data)
        ? body.data
        : body;
    const occ = data?.occupancy || [];
    return occ
      .filter((row) => {
        if (!row?.ts_id || !row.ts_owner_player_id) return false;
        const owner = String(row.ts_owner_player_id);
        const partner =
          row.partner_player_id != null ? String(row.partner_player_id) : "";
        return owner === playerId || partner === playerId;
      })
      .map(occupancyToSession)
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Collect today's sessions from user's groups and return eligible live context.
 * @param {object} params
 * @param {object} params.user
 * @param {Array} [params.groupIds]
 * @param {Array} [params.permissions]
 */
export async function getActiveLiveTrainingContext({ user, groupIds = [], permissions = [] }) {
  if (!user || !groupIds.length) return null;

  const uniqueSessions = await collectGroupSessions(groupIds);

  const candidates = [];
  for (const session of uniqueSessions) {
    // eslint-disable-next-line no-await-in-loop
    const eligible = await isUserEligibleForSession(session, user, permissions);
    if (eligible) {
      candidates.push({ session, gr_id: session.gr_id });
    }
  }

  const best = pickBestEligibleSession(candidates);
  if (!best) return null;

  const window = parseSessionWindow(best.session);
  return {
    session: best.session,
    gr_id: best.gr_id,
    windowEndMs: window?.endMs ?? null,
  };
}

/**
 * Session a player can self-register for: club group or own booking,
 * starting in the next 15 minutes or already inside the window.
 */
export async function getJoinableGroupLiveTrainingContext({
  user,
  groupIds = [],
} = {}) {
  if (!user) return null;

  const uniqueSessions = [];
  if (groupIds.length) {
    uniqueSessions.push(...(await collectGroupSessions(groupIds)));
  }
  const owned = await collectPlayerOwnedSessions(user);
  const byTs = new Map();
  [...uniqueSessions, ...owned].forEach((s) => {
    if (s?.ts_id && !byTs.has(s.ts_id)) byTs.set(s.ts_id, s);
  });
  const candidates = [...byTs.values()]
    .filter((session) => isSessionJoinableSoon(session))
    .map((session) => ({ session, gr_id: session.gr_id }));

  const best = pickBestEligibleSession(candidates);
  if (!best) return null;

  const window = parseSessionWindow(best.session);
  return {
    session: best.session,
    gr_id: best.gr_id,
    windowEndMs: window?.endMs ?? null,
  };
}

/**
 * Ensure grpinf always has groupDetails — bare { gr_id } breaks Home/Sessions UI.
 */
export { normalizeStoredGroupInfo } from "../utils/liveTrainingGroupInfo";

export function persistLiveTrainingSession(session, groupInfo) {
  if (!session?.ts_id) return;
  clearCoachLiveTrainingAutoEnterSuppression(session.ts_id);
  localStorage.setItem("rsessn", JSON.stringify(session));
  const normalized = normalizeStoredGroupInfo(groupInfo, session);
  if (normalized) {
    localStorage.setItem("grpinf", JSON.stringify(normalized));
  }
  const window = parseSessionWindow(session);
  writeLiveTrainingContext({
    ts_id: session.ts_id,
    gr_id: session.gr_id,
    windowEndMs: window?.endMs ?? null,
  });
}

export function extractGroupIdsFromUser(user, storedGroup) {
  const ids = new Set();
  if (storedGroup?.groupDetails?.gr_id) ids.add(storedGroup.groupDetails.gr_id);
  if (storedGroup?.gr_id) ids.add(storedGroup.gr_id);
  if (user?.gr_id) ids.add(user.gr_id);
  if (Array.isArray(user?.groups)) {
    user.groups.forEach((g) => {
      if (g?.gr_id) ids.add(g.gr_id);
      if (g?.groupDetails?.gr_id) ids.add(g.groupDetails.gr_id);
    });
  }
  return [...ids].filter(Boolean);
}

async function fetchUserGroupIds(user) {
  if (!user?.club_id) return [];
  try {
    const activeHomeRole = readActiveHomeRole(user);
    const mappedUserType = mapHomeRoleToUserType(activeHomeRole, user);
    const res = await homepageadmin({
      user_type: Number(mappedUserType),
      club_id: user.club_id,
      home_role: activeHomeRole,
    });
    const list = res?.data?.data?.groupList || [];
    return list
      .map((g) => g?.groupDetails?.gr_id || g?.gr_id)
      .filter(Boolean);
  } catch {
    return [];
  }
}

export async function resolveGroupIdsForLiveTraining(user) {
  const storedGroup = JSON.parse(localStorage.getItem("grpinf") || "null");
  let groupIds = extractGroupIdsFromUser(user, storedGroup);
  if (!groupIds.length) {
    groupIds = await fetchUserGroupIds(user);
  }
  return groupIds;
}

/**
 * Resolve redirect target after login — returns path or null.
 */
export async function resolveLiveTrainingRedirect(user, permissions) {
  const groupIds = await resolveGroupIdsForLiveTraining(user);
  if (!groupIds.length) return null;

  const ctx = await getActiveLiveTrainingContext({ user, groupIds, permissions });
  if (!ctx) return null;

  const storedGroup = JSON.parse(localStorage.getItem("grpinf") || "null");
  persistLiveTrainingSession(ctx.session, storedGroup || { gr_id: ctx.gr_id });
  return "/live-training";
}
